import { EVENT_WORDS, MATCH_ORDER, TARGETS, targetForEvent, type TargetKind } from "./targets";

export type { TargetKind };

export interface CommandSite {
  id: number;
  name: string;
  /** "경기인천" 등 표시용 지역명. 없으면 지역으로는 안 걸린다. */
  region?: string;
}

export interface Candidate {
  key: string;
  kind: TargetKind;
  site: CommandSite | null;
  path: string;
  /** "김포 풍무B5" — 없으면 전역 화면 */
  siteLabel: string | null;
  /** "CCTV" */
  targetLabel: string;
  /** "안전모 미착용" — 이벤트로 불렀을 때만. 안내 문구에 쓴다 */
  eventLabel: string | null;
  score: number;
}

/** 공백·중점을 지우고 소문자로. 매칭은 전부 이 형태에서 한다. */
function norm(text: string): string {
  return text.toLowerCase().replace(/[\s·․.,_/-]+/g, "");
}

/**
 * 명령에서 의미 없는 말을 걷어낸다. 조사와 명령 동사만 건드린다 —
 * 너무 많이 지우면 "안전" 같은 실제 키워드가 날아간다.
 */
const FILLER =
  /(보여줘|보여|열어줘|열어|띄워줘|띄워|가줘|가자|이동해줘|이동|알려줘|줘|해줘|주세요|please)/g;

export function stripFiller(text: string): string {
  return text.replace(FILLER, " ");
}

/**
 * 현장을 가리키는 군더더기. **질의와 현장명 양쪽에서** 같은 규칙으로 지운다 —
 * 한쪽만 지우면 "김포 풍무 현장" 이 `김포 풍무B5` 와, "수원 망포" 가 `수원 망포 현장` 과 어긋난다.
 */
const SITE_NOISE = /(현장|사업장|공사장)/g;

function siteKey(text: string): string {
  return norm(text).replace(SITE_NOISE, "");
}

/** 한 글자는 아무 말에나 걸린다. */
const MIN_TOKEN = 2;

/**
 * 영문·숫자를 **한글 소리**로 바꾼다.
 *
 * 현장명에는 `B5`·`1단지`·`3차`·`2공장`·`5구역` 처럼 영문과 숫자가 흔한데, 한국어
 * 음성인식은 그걸 소리로 돌려준다("비오"·"일단지"·"오구역"). 글자를 그대로 비교하면
 * 그 부분이 절대 안 맞아 앞의 한글만 걸리고, `1단지`/`2단지` 를 **말로 구분할 수 없다.**
 *
 * 타이핑한 "B5" 도 같은 규칙을 거치면 "비오" 가 되므로 양쪽 입력이 한 자리에서 만난다.
 */
const SOUND: Record<string, string> = {
  "0": "영",
  "1": "일",
  "2": "이",
  "3": "삼",
  "4": "사",
  "5": "오",
  "6": "육",
  "7": "칠",
  "8": "팔",
  "9": "구",
  a: "에이",
  b: "비",
  c: "씨",
  d: "디",
  e: "이",
  f: "에프",
  g: "지",
  h: "에이치",
  i: "아이",
  j: "제이",
  k: "케이",
  l: "엘",
  m: "엠",
  n: "엔",
  o: "오",
  p: "피",
  q: "큐",
  r: "알",
  s: "에스",
  t: "티",
  u: "유",
  v: "브이",
  w: "더블유",
  x: "엑스",
  y: "와이",
  z: "지",
};

function soundKey(text: string): string {
  return siteKey(text).replace(/[a-z0-9]/g, (ch) => SOUND[ch] ?? ch);
}

/**
 * 이름이 말 안에 얼마나 들어 있는지 — **가장 긴 공통 조각의 글자 수**.
 *
 * 앞에서부터만 잘라 맞추면 띄어쓰기 없는 이름을 중간·뒤 조각으로 못 부른다 —
 * `인천검단3차` 를 "검단" 으로, `제주위파크1단지` 를 "위파크" 로 부르는 게 안 됐다.
 * 이름의 모든 조각을 보되 가장 긴 것만 세면 두 경우가 한 규칙으로 처리된다.
 *
 * 이름이 통째로 들어 있으면 두 배를 준다 — "김포 풍무B5" 라고 정확히 부른 쪽이
 * "김포" 만 부른 쪽보다 확실히 위여야 한다.
 */
function overlap(query: string, full: string): number {
  if (full.length < MIN_TOKEN) return 0;
  if (query.includes(full)) return full.length * 2;

  let best = 0;
  for (let i = 0; i < full.length; i++) {
    for (let j = i + MIN_TOKEN; j <= full.length; j++) {
      const piece = full.slice(i, j);
      if (piece.length > best && query.includes(piece)) best = piece.length;
    }
  }
  return best;
}

function nameScore(query: string, name: string): number {
  // 글자 그대로와 **소리로 바꾼 것** 둘 다 보고 더 많이 걸린 쪽을 쓴다.
  // 타이핑한 "B5" 는 앞쪽이, 말한 "비오" 는 뒤쪽이 잡는다.
  return Math.max(overlap(query, siteKey(name)), overlap(soundKey(query), soundKey(name)));
}

/**
 * 말 안에서 이름이 걸리는 현장들 — 많이 걸린 순.
 *
 * 되묻기에 칩 대신 이름을 말했을 때도 같은 규칙으로 골라야 해서 밖으로 낸다.
 */
export function matchSites(input: string, sites: CommandSite[]): CommandSite[] {
  const query = norm(stripFiller(input.trim()));
  if (!query) return [];
  return sites
    .map((s) => ({ site: s, hit: nameScore(query, s.name) }))
    .filter((r) => r.hit > 0)
    .sort((a, b) => b.hit - a.hit)
    .map((r) => r.site);
}

/**
 * 명령 문자열 → 갈 수 있는 곳들.
 *
 * 서버를 타지 않는다. 이동 대상이 우리 라우트로 한정돼 있어 규칙으로 충분하다.
 *
 * @param canGo 권한 필터. 후보 단계에서 걸러 못 가는 곳을 아예 안 보여준다.
 */
export function resolveCommand(
  input: string,
  sites: CommandSite[],
  canGo: (kind: TargetKind, siteId: number | null) => boolean,
  options: { currentSiteId?: number | null } = {}
): Candidate[] {
  const query = norm(stripFiller(input.trim()));
  if (!query) return [];

  /*
    현장을 **전체 질의에서 바로** 찾는다.

    예전엔 화면 키워드를 질의에서 지운 나머지로 현장을 찾았는데, 그러면 키워드가
    현장명을 잘라먹었다 — `national` 의 "홈" 이 `래미안 홈타운` 의 "홈" 을 지워 버렸다.
    빼기를 그만두면 그 문제가 원인부터 사라진다.
  */
  const byName = sites
    .map((s) => ({ site: s, hit: nameScore(query, s.name) }))
    .filter((r) => r.hit > 0);

  // 이름이 안 걸리면 지역으로도 본다. 이름과 **같은 부분일치 규칙**을 써야
  // "인천"·"제주" 처럼 표시명("경기인천"·"제주도")의 일부만 말해도 걸린다.
  const byRegion = byName.length
    ? []
    : sites
        .filter((s) => s.region && nameScore(query, s.region) > 0)
        .map((s) => ({ site: s, hit: MIN_TOKEN }));

  const named = byName.length ? byName : byRegion;

  /*
    화면 키워드. **걸린 현장 이름 안에 들어 있는 키워드는 무시한다** —
    "홈타운 보여줘" 의 "홈" 을 전국 관제로 읽으면 아무 확인 없이 딴 데로 간다.
    이름에서 온 글자인지 진짜 화면 지시인지를 여기서 가른다.
  */
  const namedKeys = named.map((r) => siteKey(r.site.name));

  /*
    **이벤트를 화면 키워드보다 먼저 본다.** 이벤트 말이 더 좁아서다 — "영상감지" 를
    화면 키워드에 먼저 물리면 "영상"(cctv)이 먼저 걸려 카테고리 구분이 사라진다.

    현장 이름 안에 든 말은 여기서도 무시한다. 화면 키워드와 같은 이유다.
  */
  const eventHit =
    EVENT_WORDS.find((e) =>
      e.words.some((w) => {
        const key = norm(w);
        if (!query.includes(key)) return false;
        return !namedKeys.some((n) => n.includes(key));
      })
    ) ?? null;

  const kindHit = eventHit
    ? targetForEvent(eventHit.category)
    : MATCH_ORDER.find((k) =>
        TARGETS[k].keywords.some((w) => {
          const key = norm(w);
          if (!query.includes(key)) return false;
          return !namedKeys.some((n) => n.includes(key));
        })
      );

  const matchedSites = named.length
    ? named.map((r) => r.site)
    : // 현장을 안 말했으면 보고 있는 현장을 쓴다. 그것도 없으면 전 현장이 후보다.
      options.currentSiteId != null
      ? sites.filter((s) => s.id === options.currentSiteId)
      : sites;

  const hitOf = new Map(named.map((r) => [r.site.id, r.hit]));
  const bestHit = Math.max(0, ...hitOf.values());

  // 화면도 현장도 안 걸렸으면 모르는 말이다. 여기서 전 현장으로 떨어뜨리면
  // 아무 말에나 아무 데로 간다.
  if (!kindHit && !named.length) return [];

  const kinds = kindHit ? [kindHit] : MATCH_ORDER;
  const out: Candidate[] = [];

  for (const kind of kinds) {
    const spec = TARGETS[kind];

    /*
      전역 화면(전국·전체 보건관리)은 **그 화면을 콕 집어 말했거나** 현장을 아예
      대지 않았을 때 낸다. 현장을 댔다고 무조건 막으면 "인천 전체 보여줘" 처럼
      전역 키워드와 현장명을 같이 말했을 때 후보가 하나도 안 남는다.
      현장을 곁들여 말한 경우엔 아래 현장 후보가 점수로 이긴다.
    */
    const globalPath = kindHit === kind || !named.length ? spec.path(undefined) : null;
    if (globalPath && canGo(kind, null)) {
      out.push({
        key: `${kind}:global`,
        kind,
        site: null,
        path: globalPath,
        siteLabel: null,
        targetLabel: spec.label,
        eventLabel: null,
        // 현장을 안 댔고 보고 있는 현장도 없으면 전역이 맞다 — "보건관리" 한 마디는
        // 현장 중 고르라는 뜻이 아니라 전체를 보겠다는 뜻이다.
        score:
          (kindHit === kind ? 100 : 10) + (!named.length && options.currentSiteId == null ? 25 : 0),
      });
    }

    // 전국 관제처럼 현장에 속하지 않는 화면은 현장마다 하나씩 만들지 않는다.
    if (spec.global) continue;

    for (const site of matchedSites) {
      const path = spec.path(site.id);
      if (!path || !canGo(kind, site.id)) continue;
      out.push({
        key: `${kind}:${site.id}`,
        kind,
        site,
        path,
        siteLabel: site.name,
        targetLabel: spec.label,
        eventLabel: eventHit?.label ?? null,
        score:
          (kindHit === kind ? 100 : 0) +
          // 이름이 가장 많이 걸린 현장만 올린다. "김포 풍무" 는 두 김포에 다 걸리지만
          // `김포 풍무B5`(4자)가 `김포 걸포3`(2자)를 이긴다.
          (hitOf.get(site.id) === bestHit && bestHit > 0 ? 20 : 0) +
          // 현장을 안 댔으면 보고 있는 현장이 기준이다.
          (!named.length && site.id === options.currentSiteId ? 30 : 0) +
          (kind === "dashboard" ? 5 : 0),
      });
    }
  }

  return out.sort((a, b) => b.score - a.score || a.key.localeCompare(b.key));
}

/**
 * 해석 결과. **하나의 판단**이지 목록이 아니다.
 *
 * 사용자에게 "갈 수 있는 화면" 을 늘어놓지 않는다 — 그건 우리 라우트 표를 그대로
 * 보여주는 것이고, 말하면 알아서 간다는 전제와 어긋난다. 못 알아들었을 때와
 * 현장이 여럿일 때만 되묻는다.
 */
export type Interpretation =
  | { type: "go"; path: string; say: string }
  | { type: "ask-site"; sites: CommandSite[]; say: string; pending: TargetKind }
  | { type: "unknown"; say: string };

/** 되묻기에 한 번에 보여줄 현장 수. 넘으면 더 좁혀 달라고 한다. */
export const ASK_LIMIT = 8;

export function interpret(
  input: string,
  sites: CommandSite[],
  canGo: (kind: TargetKind, siteId: number | null) => boolean,
  options: { currentSiteId?: number | null } = {}
): Interpretation {
  if (!input.trim()) return { type: "unknown", say: "" };

  const candidates = resolveCommand(input, sites, canGo, options);
  const best = candidates[0];
  if (!best) {
    /*
      이름은 알아들었는데 후보가 빈 경우 = **그 화면(기능) 권한이 없다.**

      현장 자체의 권한은 여기까지 오지 않는다 — `/api/sites` 가 이미 권한으로 걸러서
      주므로, 목록에 있는 현장은 전부 들어갈 수 있는 곳이다. 반대로 권한 없는 현장은
      목록에 아예 없어 `matchSites` 도 못 찾는다. 그래서 아래 일반 문구가 두 가지를
      겸한다 — 모르는 말일 때와, 목록 밖 현장을 말했을 때.
    */
    const known = matchSites(input, sites)[0];
    if (known) {
      return { type: "unknown", say: `${known.name}에서 그 화면을 볼 권한이 없어요.` };
    }
    return {
      type: "unknown",
      say: "그 이름의 현장을 못 찾았어요. 접근 권한이 있는 현장만 찾을 수 있어요.",
    };
  }

  /*
    **점수가 같은** 현장이 여럿이면 되묻는다. 점수로 하나 골라 조용히 이동하면
    틀린 현장으로 갔을 때 알아채기 어렵다 — 남의 현장을 보고 있는 건 조용히 넘길
    실수가 아니다. 이름이 더 많이 걸린 게 있으면 모호한 게 아니므로 그냥 간다.
  */
  const tied = candidates.filter((c) => c.kind === best.kind && c.site && c.score === best.score);
  const distinct = new Map(tied.map((c) => [c.site!.id, c.site!]));

  // 1등이 전역 화면이면 물을 게 없다.
  if (best.site && distinct.size > 1) {
    const list = [...distinct.values()];
    // 너무 많으면 고르라고 내밀 게 아니라 더 좁혀 달라고 해야 한다 — 수십 개를
    // 늘어놓으면 입력창이 화면 밖으로 밀린다.
    if (list.length > ASK_LIMIT) {
      return {
        type: "unknown",
        say: `그 이름의 현장이 ${list.length}곳이에요. 현장 이름을 조금 더 말해 주세요.`,
      };
    }
    return { type: "ask-site", sites: list, say: "어느 현장인가요?", pending: best.kind };
  }

  return { type: "go", path: best.path, say: sayFor(best) };
}

function sayFor(c: Candidate): string {
  if (!c.siteLabel) return `${c.targetLabel}로 이동할게요.`;
  // 이벤트로 불렀으면 알아들은 이름을 되돌려 준다 — 엉뚱한 걸로 알아들었을 때
  // 화면이 바뀐 뒤에야 눈치채는 일을 줄인다.
  if (c.eventLabel) return `${c.siteLabel} ${c.eventLabel} 보러 갈게요.`;
  return c.kind === "dashboard"
    ? `${siteWithSuffix(c.siteLabel)}으로 이동할게요.`
    : `${c.siteLabel} ${c.targetLabel}로 이동할게요.`;
}

/** 이름이 이미 "현장" 으로 끝나면 덧붙이지 않는다 — "수원 망포 현장 현장으로" 가 된다. */
export function siteWithSuffix(name: string): string {
  return name.trim().endsWith("현장") ? name.trim() : `${name.trim()} 현장`;
}
